// 情報入力画面 兼 確認画面

<template lang="pug">
.outro-page-register(v-if='ready')

  //- スマホ向けのカート情報
  ps-cart-info.cart-info-mobile(
    v-if='screenType === "mobile"',
    :num='cartNum', :sum='cartTotal', :details='cartDetails', :photosSummary='cartPhotosSummary'
  )

  ps-row(:gutter='20')

    //- メインの部分
    ps-col.main

      //- 登録ステップ
      //- PAY.JPの確定にDOM要素が必要なので、登録ステップと確認ステップを分けず、
      //- v-showで同一のコンポーネント内で表示を切り替える形にする
      register-step(
        v-show='!showConfirm',
        :payjp-client-secret='payjpClientSecret',
        @updateUserChildren='userChildren = $event',
        @goToNext='goToNext',
        @goToPrev='goToPrev',
        ref='registerStepComponent'
      )

      //- 確認ステップ
      //- 登録ステップで取得したお子様の情報をpropsで渡す
      confirm-step(
        v-show='showConfirm',
        :userChildren='userChildren',
        @goToPrev='goToPrev',
        @commitOrder='commitOrder',
        :buttonLock='buttonLock'
      )

    //- サイドのカート情報(PC向け)
    ps-col.side(v-if='screenType !== "mobile"', :span='0')
      ps-fixed-region(:threshold='20')
        .cart-info-outer
          ps-cart-info(:num='cartNum', :sum='cartTotal', :details='cartDetails', :photosSummary='cartPhotosSummary', transparent)
            ps-button(
              full-width,
              size='small',
              text-centered,
              @click='goToPrev'
            )
              ps-icon(icon='chevron-circle-left')
              | {{ showConfirm ? '内容修正' : '戻る' }}
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import RegisterStep from './components/register-step.vue'
import ConfirmStep from './components/confirm-step.vue'
import * as errorCodes from '@/errors/codes'
// 期間外販売についての確認ダイアログ
import ConfirmOverdue from '@/dialogs/contents/confirm-overdue'
import createPayjpFlow from '@/api/v2-bridge/create-payjp-flow'

export default {
  name: 'OutroPageRegister',

  // register フォームを経由せずに ?phase=confirm に直接アクセスした場合（リロード・別ページからの遷移）は
  // クエリなしの register へ強制リダイレクトする
  beforeRouteEnter(to, from, next) {
    console.log('beforeRouteEnter', to, from)
    if (to.name === 'OutroPageRegister' && to.query.phase === 'confirm') {
      next({ name: 'OutroPageRegister', params: to.params, replace: true })
    } else {
      next()
    }
  },

  components: {
    RegisterStep,
    ConfirmStep,
  },

  data() {
    return {
      userChildren: null,
      ready: false,
      // 注文確定ボタンのロック用のフラグ
      buttonLock: false,
      // Pay.JPの決済フローのID
      payjpPaymentFlowId: null,
      // Pay.JPのクライアントシークレット
      payjpClientSecret: null,
    }
  },

  computed: {
    ...mapGetters({
      // 販売の情報
      saleIsInTerm: 'sale/isInTerm',
      saleIsInExtra: 'sale/isInExtra',
      isPouch: 'sale/isPouch',
      isPublic: 'sale/isPublic',
      isIndividual: 'sale/isIndividual',
      // 新しいお子様情報管理を利用するか
      useNewChildrenInfoSystem: 'sale/useNewChildrenInfoSystem',
      // 期間外販売の手数料
      overdueCharge: 'sale/overdueCharge',
      // 端末の種類
      screenType: 'app/screenType',
      role: 'app/role',
      // カート関連の情報
      cartNum: 'cart/num',
      photoNum: 'cart/photoNum',
      cartTotal: 'cart/total',
      cartDetails: 'cart/details',
      cartPhotosSummary: 'cart/photosSummary',
      cartOverdueNum: 'cart/overdueNum',
      // 年間販売向けの確認ダイアログを表示するかどうか
      showFirstTimeConfirmOverdueDialog: 'browsing/showFirstTimeConfirmOverdueDialog',
    }),
    // 読み書きする可能性があるストア情報

    // ルートパラメータからsaleIdを取得
    saleId() {
      return this.$route.params.saleId
    },
    // 確認画面を表示するか
    showConfirm() {
      return this.$route.query.phase === 'confirm'
    },
  },

  async created() {
    try {
      // 最初にsaleの情報を取得することで販売期間ないかどうかをチェック
      await this.initSaleAsync({ id: this.saleId })
      // 販売期間または販売猶予期間かどうか
      if (!this.saleIsInTerm && !this.saleIsInExtra) {
        throw new Error(errorCodes.SALE_IS_OUT_OF_TERM)
      }

      await Promise.all([this.createPayjpFlowAsync(), this.getCartInfoAsync()])
    } catch (error) {
      this.setError(error)
    }

    // 写真の点数が0枚ならアラートを出してカートに戻す
    if (this.photoNum === 0) {
      alert(
        '買い物カゴに写真がありません。ご注文手続きを進めるには1枚以上の写真をカゴに追加してください。'
      )
      this.$router.push({ name: 'OutroPageCart', params: { id: this.saleId } })
      return
    }

    // 年間販売の期間外販売手数料の対象となる写真が存在する場合に確認ダイアログ表示
    if (this.cartOverdueNum > 0 && this.overdueCharge && this.showFirstTimeConfirmOverdueDialog) {
      this.$psdialog.open({
        title: '確認',
        component: ConfirmOverdue,
        props: {
          overdueNum: this.cartOverdueNum,
          overdueCharge: this.overdueCharge,
        },
        size: 's',
        closeByClickBg: true,
        closeButton: true,
      })
      this.$store.commit('browsing/setShowFirstTimeConfirmOverdueDialog', false)
    }

    this.ready = true
  },

  mounted() {
    // ページを離れるときに確認のダイアログを出す
    window.addEventListener('beforeunload', this.unload, false)
  },

  beforeDestroy() {
    // イベントリスナーをクリーンアップ
    window.removeEventListener('beforeunload', this.unload, false)
  },

  methods: {
    ...mapActions({
      // 販売の情報を取得
      initSaleAsync: 'sale/initAsync',
      // カートの情報を取得
      initCartAsync: 'cart/initAsync',
      clearCartAll: 'cart/clearAll',
      clearOrderAll: 'order/clearAll',
      // 注文を確定
      commitAsync: 'order/commitAsync',
      // エラーをセット
      setError: 'app/setError',
    }),

    // Pay.JPの決済フローを呼び出すためのメソッド
    async createPayjpFlowAsync() {
      try {
        const response = await createPayjpFlow(null, {
          saleId: Number(this.saleId),
        })
        if (response.ok && response.payload) {
          this.payjpClientSecret = response.payload.clientSecret
          this.payjpPaymentFlowId = response.payload.paymentFlowId
        } else {
          throw new Error('Failed to create Pay.JP flow')
        }
      } catch (error) {
        console.error('Failed to create Pay.JP flow', error)
        throw error
      }
    },

    // カートの情報を取得するアクションを呼び出す
    async getCartInfoAsync() {
      // 何らかの理由でカートの中身に変更があった場合に備え強制的に初期化
      await this.initCartAsync(true)
    },

    // 次のページに進む
    goToNext() {
      if (!this.showConfirm) {
        // 登録画面の場合はクエリを変更して確認画面に進む
        this.$router.push({
          name: 'OutroPageRegister',
          params: { id: this.saleId },
          query: { phase: 'confirm' },
        })
      } else {
        // 確認画面の場合は注文完了ページに進む
        this.$router.push({ name: 'OutroPageCompleted', params: { id: this.saleId } })
      }
    },

    // 前のページに戻る
    goToPrev() {
      if (!this.showConfirm) {
        // 登録画面の場合はカートに戻る
        // ヒストリバックだと、前ページが無い場合に戻れなくなってしまうため
        this.$router.push({ name: 'OutroPageCart', params: { id: this.saleId } })
      } else {
        // 確認画面の場合はヒストリをバックする
        this.$router.back()
      }
    },

    // アンロード時のイベントハンドラ
    unload(e) {
      // 確認画面にいるときにだけ、ページを離れるときの確認ダイアログを出す
      if (this.showConfirm && !this.buttonLock) {
        e.returnValue =
          '再読み込み、または別のサイトへの移動を行うと、入力内容がクリアされますがよろしいですか？'
      }
    },

    // 注文確定の処理
    async commitOrder() {
      // 確認画面のときだけ注文確定処理を実行
      if (!this.showConfirm) return
      if (this.buttonLock) return
      this.buttonLock = true

      try {
        const { orderCode, callbackUrl } = await this.commitAsync({
          saleId: this.saleId,
          totalAmount: this.cartTotal,
          isPouch: this.isPouch,
          isPublic: this.isPublic,
          isIndividual: this.isIndividual,
          useNewChildrenInfoSystem: this.role === 'member' && this.useNewChildrenInfoSystem,
          payjpPaymentFlowId: this.payjpPaymentFlowId,
        })

        // コールバックURLの有無で場合分け
        if (!callbackUrl) {
          // ストアのカート情報をクリア
          this.clearCartAll()
          // ストアの注文情報をクリア
          this.clearOrderAll()

          // コンビニ決済の完了画面に遷移
          this.$router.push({
            name: 'OutroPageCompleted',
            params: {
              saleId: this.saleId,
              code: orderCode,
            },
          })

          return
        } else {
          // Pay.JPの決済画面に遷移
          await this.$refs.registerStepComponent.confirmPayment(callbackUrl)
        }
      } catch (error) {
        // 復帰可能なエラーかどうかで場合分け
        if (
          error.message === errorCodes.ORDER_RETRYABLE_CREDIT_ERROR ||
          error.message === errorCodes.ORDER_RETRYABLE_ERROR ||
          error.message === errorCodes.ORDER_RETRYABLE_CREDIT_EXPIRY
        ) {
          this.setError(error)
        } else {
          console.error('Failed to commit order', error)
          this.setError(new Error(errorCodes.ORDER_ERROR))
        }
      } finally {
        this.buttonLock = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
@import '../../../../sass/variables.sass'
@import '../../../../sass/mixins.sass'

.outro-page-register

  .cart-info-mobile
    margin-bottom: 1.5rem

  .main
    background-color: $white
    padding: 1.5rem 1rem
    border-radius: $radius-small
    box-shadow: 0 1px 6px 2px rgba(0, 0, 0, .25)
    +mobile
      padding: 1rem 0.5rem
      box-shadow: 0 1px 1px 1px rgba(0, 0, 0, .3)

  .side
    width: 260px
    .cart-info-outer
      background-color: #e4f7b7
</style>
